<template>
    <div>
        <div class="mb-3 border-bottom pb-3 text-right">
            <button type="button" class="btn" @click="setAutoRefresh" :disabled="loading"
                    :class="{'disabled': loading, 'btn-outline-success': !auto_refresh, 'btn-success': auto_refresh }">
                <i class="fas fa-circle-notch mr-1 fa-spin" v-if="auto_refresh"></i>
                <i class="fas fa-sync-alt mr-1" v-else></i>
                Auto refresh
            </button>
            <button type="button" class="btn btn-outline-primary" @click="fetch" :disabled="loading"
                    :class="{'disabled': loading}">
                <i class="fas fa-redo mr-1"></i>
                Refresh
            </button>
        </div>

        <div class="d-flex justify-content-between mb-2">
            <ul class="nav nav-tabs flex-fill">
                <li class="nav-item" style="min-width: 100px;">
                    <a
                        class="nav-link"
                        :class="tabindex === 'all' ? 'active disabled' : ''"
                        href="#"
                        @click.prevent="changeTab('all')"
                    >All <span v-if="statistics.all">({{ statistics.all }})</span>
                    </a>
                </li>
                <li class="nav-item" style="min-width: 130px;">
                    <a
                        class="nav-link"
                        :class="tabindex === 'pending' ? 'active disabled' : ''"
                        href="#"
                        @click.prevent="changeTab('pending')"
                    >1. Pending <span v-if="statistics.pending">({{ statistics.pending }})</span></a>
                </li>
                <li class="nav-item" style="min-width: 130px;">
                    <a
                        class="nav-link"
                        :class="tabindex === 'waiting' ? 'active disabled' : ''"
                        href="#"
                        @click.prevent="changeTab('waiting')"
                    >2. Waiting <span v-if="statistics.waiting">({{ statistics.waiting }})</span></a>
                </li>
                <li class="nav-item" style="min-width: 130px;">
                    <a
                        class="nav-link"
                        :class="tabindex === 'running' ? 'active disabled' : ''"
                        href="#"
                        @click.prevent="changeTab('running')"
                    >3. Running <span v-if="statistics.running">({{ statistics.running }})</span></a>
                </li>
                <li class="nav-item" style="min-width: 130px;">
                    <a
                        class="nav-link"
                        :class="tabindex === 'completed' ? 'active disabled' : ''"
                        href="#"
                        @click.prevent="changeTab('completed')"
                    >4. Completed <span v-if="statistics.completed">({{ statistics.completed }})</span></a>
                </li>
            </ul>
            <ul class="nav nav-tabs">
                <li class="nav-item" style="min-width: 80px;">
                    <a
                        class="nav-link"
                        :class="tabindex === 'error' ? 'active disabled' : ''"
                        href="#"
                        @click.prevent="changeTab('error')"
                    >Errors <span v-if="statistics.error">({{ statistics.error }})</span></a>
                </li>
                <li class="nav-item" style="min-width: 100px;">
                    <a
                        class="nav-link"
                        :class="tabindex === 'stopped' ? 'active disabled' : ''"
                        href="#"
                        @click.prevent="changeTab('stopped')"
                    >Stopped <span v-if="statistics.stopped">({{ statistics.stopped }})</span></a>
                </li>
            </ul>
        </div>

        <div class="alert alert-danger" v-if="serverError">
            {{ serverError }}
        </div>

        <div class="form-group">
            <label for="search" class="sr-only">Search</label>
            <input type="text" name="search" id="search" class="form-control" placeholder="Search performer"
                   v-model="filters.search" @input="search">
        </div>

        <spinner class="py-5" v-if="loading"/>
        <div class="table-responsive">
            <table class="table table-bordered table-middle" v-if="!loading && campaigns.length">
                <thead>
                    <tr>
                        <th>
                            <div class="d-flex justify-content-between align-items-center">
                                <a href="#" @click.prevent="sortBy('performer')">
                                    Performer
                                </a>
                                <div v-if="filters.sort_by === 'performer'">
                                    <i class="fas fa-sort-up" v-if="filters.direction === 'asc'"></i>
                                    <i class="fas fa-sort-down" v-if="filters.direction === 'desc'"></i>
                                </div>
                                <div v-else><i class="fas fa-sort"></i></div>
                            </div>
                        </th>
                        <th>Status</th>
                        <th>Members</th>
                        <th>
                            <div class="d-flex justify-content-between align-items-center">
                                <a href="#" @click.prevent="sortBy('created_at')">
                                    Created at
                                </a>
                                <div v-if="filters.sort_by === 'created_at'">
                                    <i class="fas fa-sort-up" v-if="filters.direction === 'asc'"></i>
                                    <i class="fas fa-sort-down" v-if="filters.direction === 'desc'"></i>
                                </div>
                                <div v-else><i class="fas fa-sort"></i></div>
                            </div>
                        </th>
                        <th>
                            <div class="d-flex justify-content-between align-items-center">
                                <a href="#" @click.prevent="sortBy('updated_at')">
                                    Updated at
                                </a>
                                <div v-if="filters.sort_by === 'updated_at'">
                                    <i class="fas fa-sort-up" v-if="filters.direction === 'asc'"></i>
                                    <i class="fas fa-sort-down" v-if="filters.direction === 'desc'"></i>
                                </div>
                                <div v-else><i class="fas fa-sort"></i></div>
                            </div>
                        </th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="campaign in campaigns">
                        <td class="text-left">
                            <div class="mb-2">
                                <a :href="campaign.links.messages" class="font-weight-bold">
                                    {{ campaign.performer.nickname }}
                                </a>
                            </div>
                            <progress-bar :value="campaign.progress" v-if="campaign.progress"></progress-bar>
                        </td>
                        <td>
                            <campaign-status :status="campaign.status"></campaign-status>
                        </td>
                        <td>
                            <div class="font-weight-bold">
                                {{ campaign.inactive_members_count }} /
                                {{ campaign.members_count }}
                            </div>
                            <div v-if="campaign.estimated_time">
                                <small>ET: {{ campaign.estimated_time }}</small>
                            </div>
                        </td>
                        <td>{{ campaign.created_at }}</td>
                        <td>{{ campaign.updated_at }}</td>
                        <td class="text-center">
                            <button type="button" class="btn btn-success btn-sm" v-if="campaign.can_start"
                                    @click="startCampaign(campaign)">
                                <i class="fas fa-play mr-1"></i> Start
                            </button>
                            <button type="button" class="btn btn-danger btn-sm" v-if="campaign.can_stop"
                                    @click="stopCampaign(campaign)">
                                <i class="fas fa-stop mr-1"></i> Stop
                            </button>
                            <button type="button" class="btn btn-danger btn-sm" v-if="campaign.can_start"
                                    @click="removeCampaign(campaign)">
                                <i class="fas fa-trash mr-1"></i> Delete
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <paginate
            v-if="!loading && total > 1 && campaigns.length"
            :page-count="total"
            :click-handler="changePage"
            :container-class="'pagination'"
            :page-class="'page-item'"
            :page-link-class="'page-link'"
            :prev-link-class="'page-link'"
            :next-link-class="'page-link'"
            :prev-text="'Prev'"
            :next-text="'Next'">
        </paginate>

        <h4
            class="text-center text-muted py-5 border m-0"
            v-if="!loading && !campaigns.length"
        >No records.</h4>
    </div>
</template>

<script>
import SweetAlert from "../SweetAlert";

export default {
    data() {
        return {
            campaigns: [],
            statistics: {},
            loading: false,
            tabindex: 'pending',
            auto_refresh: false,
            total: 0,
            serverError: null,
            filters: {
                page: 1,
                sort_by: null,
                direction: 'asc',
                search: '',
            }
        };
    },

    methods: {
        setAutoRefresh() {
            this.auto_refresh = !this.auto_refresh;
            this.$emit('auto_refresh', this.auto_refresh);
        },
        stopAutoRefresh() {
            this.auto_refresh = false;
            this.$emit('auto_refresh', this.auto_refresh);
        },
        search: _.debounce(function () {
            this.filters.page = 1;
            this.fetch(false);
        }, 500),
        changeTab(tabIndex) {
            this.tabindex = tabIndex;
            this.filters.page = 1;
            this.filters.sort_by = null;
            this.filters.search = '';
            this.fetch(true);
            this.stopAutoRefresh();
        },
        changePage(pageNum) {
            this.filters.page = pageNum;
            this.fetch(false);
            this.stopAutoRefresh();
        },
        sortBy(field) {
            this.filters.sort_by = field;
            this.filters.direction = this.filters.direction === 'asc' ? 'desc' : 'asc';
            this.fetch(false);
        },
        fetch(withLoader = true) {
            if (withLoader) {
                this.loading = true;
                this.campaigns = [];
                // this.statistics = {};
            }

            this.serverError = null;
            return axios.get(`/api/campaigns/${this.tabindex}`, {
                params: this.filters,
            }).then(response => {
                this.campaigns = response.data.campaigns.data;
                this.total = response.data.campaigns.pagination.total_pages;
                this.filters.page = response.data.campaigns.pagination.current_page;
                this.statistics = response.data.statistics;
                this.loading = false;
            }).catch(error => {
                this.loading = false;
                if (error.response) {
                    this.serverError = error.response.data.message;
                } else {
                    this.serverError = 'Server error';
                }
            });
        },
        startCampaign(campaign) {
            const vm = this;
            SweetAlert.confirm({
                text: 'Do you want to start this campaign?',
                confirmButtonText: 'Yes, start campaign!',
            }).then(result => {
                if (result.isConfirmed) {
                    return axios.post(`/api/campaigns/${campaign.id}/start`).then(() => {
                        vm.fetch(false);
                        SweetAlert.success({title: 'Started!'});
                    });
                }
            }).catch((error) => {
                if (error.response) {
                    SweetAlert.error({text: error.response.data.message});
                } else {
                    SweetAlert.error({text: 'Server error'});
                }
            });
        },
        stopCampaign(campaign) {
            const vm = this;
            SweetAlert.confirm({
                text: 'Do you want to stop this campaign?',
                confirmButtonText: 'Yes, stop campaign!',
            }).then(result => {
                if (result.isConfirmed) {
                    return axios.post(`/api/campaigns/${campaign.id}/stop`).then(() => {
                        vm.fetch(false);
                        SweetAlert.success({title: 'Stopped!'});
                    });
                }
            }).catch((error) => {
                if (error.response) {
                    SweetAlert.error({text: error.response.data.message});
                } else {
                    SweetAlert.error({text: 'Server error'});
                }
            });
        },
        removeCampaign(campaign) {
            SweetAlert.confirm({
                text: 'Do you want to remove this campaign?',
                confirmButtonText: 'Yes, delete campaign!',
            }).then(result => {
                if (result.isConfirmed) {
                    return axios.delete(`/api/campaigns/${campaign.id}`).then(() => {
                        this.fetch(false);
                        SweetAlert.success({title: 'Deleted!'});
                    });
                }
            }).catch((error) => {
                if (error.response) {
                    SweetAlert.error({text: error.response.data.message});
                } else {
                    SweetAlert.error({text: 'Server error'});
                }
            });
        }
    },

    mounted() {
        this.fetch();
        this.$on('auto_refresh', (payload) => {
            if (payload) {
                this.timer = setInterval(() => {
                    this.fetch(false);
                }, 20000);
            } else {
                clearInterval(this.timer);
            }
        });
    }
}
</script>

<style scoped>
.table tr:first-child td {
    border-top: 0;
}
</style>
